import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, CalendarCheck, Download, Loader2, DoorOpen, LogOut, CalendarPlus, Ban, Search, Undo2 } from "lucide-react";
import api, { errMsg, downloadBlob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProjects } from "@/context/ProjectContext";
import { PageHeader, SectionCard, Loading, EmptyState, Money } from "@/components/Common";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatTanggal, formatTanggalWaktu, LEASE_STATUS } from "@/lib/format";
import { toast } from "sonner";

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));
function wibLocalNow() {
  const d = new Date();
  const wib = new Date(d.getTime() + d.getTimezoneOffset() * 60000 + 7 * 3600000);
  return wib.toISOString().slice(0, 16);
}
const toIsoWib = (v) => (v ? `${v}:00+07:00` : v);

const BK_STATUS = {
  dp_held: { label: "DP Diterima (Aktif)", c: "#c2410c", bg: "#fff7ed" },
  converted: { label: "Lunas → Jadi Sewa", c: "#047857", bg: "#ecfdf5" },
  expired: { label: "Kedaluwarsa (DP Hangus)", c: "#b91c1c", bg: "#fef2f2" },
  cancelled: { label: "Dibatalkan", c: "#475569", bg: "#f1f5f9" },
};

export default function BookingSewa() {
  const { isSuperadmin } = useAuth();
  const { projectId, projectParam } = useProjects();
  const location = useLocation();
  const qc = useQueryClient();
  const [bookingOpen, setBookingOpen] = useState(false);
  const [preRoom, setPreRoom] = useState(null);
  const [settleBooking, setSettleBooking] = useState(null);
  const [moveinLease, setMoveinLease] = useState(null);
  const [extLease, setExtLease] = useState(null);
  const [exitLease, setExitLease] = useState(null);
  const [depositLease, setDepositLease] = useState(null);
  const [cancelBooking, setCancelBooking] = useState(null);
  const [directOpen, setDirectOpen] = useState(false);
  const [leaseQuery, setLeaseQuery] = useState("");
  const [leaseStatus, setLeaseStatus] = useState("all");
  const [endFrom, setEndFrom] = useState("");
  const [endTo, setEndTo] = useState("");

  useEffect(() => {
    if (location.state?.roomId) { setPreRoom(location.state.roomId); setBookingOpen(true); }
  }, [location.state]);

  const { data: bookings = [], isLoading: bLoading } = useQuery({
    queryKey: ["bookings", projectId],
    queryFn: async () => (await api.get("/bookings", { params: projectParam })).data,
  });
  const { data: leases = [], isLoading: lLoading } = useQuery({
    queryKey: ["leases", projectId],
    queryFn: async () => (await api.get("/leases", { params: projectParam })).data,
  });
  const activeBookings = bookings.filter((b) => b.status === "dp_held");
  const historyBookings = bookings.filter((b) => b.status === "cancelled" || b.status === "expired");
  const totalForfeit = historyBookings.reduce((s, b) => s + (b.dp_amount || 0), 0);

  const openLeaseReceipt = async (leaseId) => {
    try {
      const res = await api.get(`/leases/${leaseId}/receipt`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const leaseEndIncl = (l) => {
    if (!l.lease_end_exclusive) return null;
    const d = new Date(l.lease_end_exclusive);
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  };

  const filteredLeases = useMemo(() => {
    const q = leaseQuery.trim().toLowerCase();
    return leases.filter((l) => {
      if (leaseStatus !== "all" && l.status !== leaseStatus) return false;
      if (q && !(l.resident_name || "").toLowerCase().includes(q) && !String(l.room_number || "").toLowerCase().includes(q)) return false;
      if (endFrom || endTo) {
        const e = leaseEndIncl(l);
        if (!e) return false;
        if (endFrom && e < endFrom) return false;
        if (endTo && e > endTo) return false;
      }
      return true;
    });
  }, [leases, leaseQuery, leaseStatus, endFrom, endTo]);

  const leaseStatusOptions = useMemo(() => {
    const present = new Set(leases.map((l) => l.status));
    return Object.keys(LEASE_STATUS).filter((k) => present.has(k));
  }, [leases]);

  const exportLeases = async (fmt) => {
    try {
      const res = await api.get(`/leases/export.${fmt}`, {
        params: {
          status: leaseStatus === "all" ? undefined : leaseStatus,
          q: leaseQuery.trim() || undefined,
          end_from: endFrom || undefined, end_to: endTo || undefined,
        },
        responseType: "blob",
      });
      downloadBlob(res.data, `daftar-sewa.${fmt}`);
    } catch { toast.error("Gagal mengekspor daftar sewa."); }
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Booking & Sewa" description="Alur DP: pilih kamar → DP → survei → pelunasan → masuk. Atau Langsung Sewa tanpa DP (bayar penuh di awal)."
        actions={<div className="flex flex-wrap gap-2">
          <Button onClick={() => { setPreRoom(null); setBookingOpen(true); }} data-testid="create-booking-button"><Plus className="mr-2 h-4 w-4" /> Booking dengan DP</Button>
          <Button variant="outline" onClick={() => setDirectOpen(true)} data-testid="create-direct-rent-button"><DoorOpen className="mr-2 h-4 w-4" /> Langsung Sewa — Tanpa DP</Button>
        </div>} />

      <Tabs defaultValue="booking">
        <TabsList>
          <TabsTrigger value="booking" data-testid="tab-booking">Booking ({activeBookings.length})</TabsTrigger>
          <TabsTrigger value="sewa" data-testid="tab-sewa">Sewa ({leases.length})</TabsTrigger>
          <TabsTrigger value="riwayat" data-testid="tab-riwayat">Riwayat ({historyBookings.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="booking" className="mt-4">
          {bLoading ? <Loading /> : activeBookings.length === 0 ? (
            <EmptyState title="Belum ada booking aktif" description="Buat booking baru untuk menahan kamar. Booking yang sudah lunas pindah ke tab Sewa; yang dibatalkan/kedaluwarsa tercatat di Rekap Keuangan." icon={CalendarCheck} />
          ) : (
            <div className="space-y-3">
              {activeBookings.map((b) => {
                const st = BK_STATUS[b.status] || { label: b.status, c: "#475569", bg: "#f1f5f9" };
                return (
                  <SectionCard key={b.id} className="!p-4" testid={`booking-row-${b.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{b.resident_name}</span>
                          <span className="rounded-full px-2 py-0.5 text-xs font-semibold" style={{ color: st.c, backgroundColor: st.bg }}>{st.label}</span>
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">{b.project_name} · {b.house_name} · Kamar {b.room_number} · {b.package_months} bln · {b.whatsapp}</div>
                        <div className="mt-2 flex flex-wrap gap-4 text-sm">
                          <span>Total: <Money value={b.total_billed} /></span>
                          <span>DP: <Money value={b.dp_amount} /></span>
                          <span className="font-semibold">Sisa: <Money value={b.remaining} /></span>
                          <span className="text-muted-foreground">Batas: {formatTanggalWaktu(b.deadline_at)}</span>
                        </div>
                      </div>
                      {b.status === "dp_held" && (
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" onClick={() => setSettleBooking(b)} data-testid={`settle-button-${b.id}`}>Lanjut Pelunasan</Button>
                          {isSuperadmin && (
                            <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => setCancelBooking(b)} data-testid={`cancel-booking-button-${b.id}`}>
                              <Ban className="mr-1.5 h-4 w-4" /> Batalkan Booking
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </SectionCard>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="sewa" className="mt-4">
          <div className="mb-3 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-[200px] flex-1">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={leaseQuery} onChange={(e) => setLeaseQuery(e.target.value)} placeholder="Cari nama penghuni / nomor kamar…" className="pl-8" data-testid="lease-search-input" />
              </div>
              <Select value={leaseStatus} onValueChange={setLeaseStatus}>
                <SelectTrigger className="w-full sm:w-[200px]" data-testid="lease-status-filter"><SelectValue placeholder="Semua status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua status</SelectItem>
                  {leaseStatusOptions.map((k) => <SelectItem key={k} value={k}>{LEASE_STATUS[k].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <Label className="text-xs text-muted-foreground">Kontrak berakhir dari</Label>
                <Input type="date" value={endFrom} onChange={(e) => setEndFrom(e.target.value)} className="h-9 w-[160px]" data-testid="lease-end-from" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">sampai</Label>
                <Input type="date" value={endTo} onChange={(e) => setEndTo(e.target.value)} className="h-9 w-[160px]" data-testid="lease-end-to" />
              </div>
              <Button variant="outline" size="sm" onClick={() => {
                const n = new Date(); const t = new Date(); t.setDate(t.getDate() + 30);
                setEndFrom(n.toISOString().slice(0, 10)); setEndTo(t.toISOString().slice(0, 10)); setLeaseStatus("active");
              }} data-testid="lease-preset-30">Berakhir ≤30 hari</Button>
              {(endFrom || endTo || leaseQuery || leaseStatus !== "all") && (
                <Button variant="ghost" size="sm" onClick={() => { setEndFrom(""); setEndTo(""); setLeaseQuery(""); setLeaseStatus("all"); }} data-testid="lease-clear-filters">Reset</Button>
              )}
              <div className="ml-auto flex gap-2">
                <Button variant="outline" size="sm" onClick={() => exportLeases("csv")} data-testid="lease-export-csv"><Download className="mr-1.5 h-4 w-4" /> Ekspor CSV</Button>
                <Button variant="outline" size="sm" onClick={() => exportLeases("xlsx")} data-testid="lease-export-xlsx"><Download className="mr-1.5 h-4 w-4" /> Ekspor Excel</Button>
              </div>
            </div>
          </div>
          {lLoading ? <Loading /> : leases.length === 0 ? (
            <EmptyState title="Belum ada sewa" icon={DoorOpen} />
          ) : filteredLeases.length === 0 ? (
            <EmptyState title="Tidak ada sewa yang cocok" description="Sesuaikan kata kunci pencarian atau filter status." icon={Search} />
          ) : (
            <div className="space-y-3">
              {filteredLeases.map((l) => (
                <SectionCard key={l.id} className="!p-4" testid={`lease-row-${l.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold">{l.resident_name}</span>
                        <StatusBadge kind="lease" value={l.status} />
                      {l.direct_rent && <span className="rounded-full bg-orange-50 px-2 py-0.5 text-xs font-semibold text-orange-700">Langsung Sewa</span>}
                        <DueBadge lease={l} />
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">{l.project_name} · {l.house_name} · Kamar {l.room_number} · {l.package_months} bln</div>
                      <div className="mt-2 text-sm">Periode: <span className="font-medium">{l.period_label}</span></div>
                      {l.settlement_date && <div className="text-xs text-muted-foreground">Tanggal pelunasan: {formatTanggal(l.settlement_date)}</div>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => openLeaseReceipt(l.id)} data-testid={`lease-receipt-button-${l.id}`}><Download className="mr-1.5 h-4 w-4" /> Unduh Struk</Button>
                      {l.status === "awaiting_movein" && <Button size="sm" onClick={() => setMoveinLease(l)} data-testid={`movein-button-${l.id}`}><DoorOpen className="mr-1.5 h-4 w-4" /> Catat Masuk</Button>}
                      {l.status === "active" && !leases.some((x) => x.parent_lease_id === l.id && x.status === "scheduled") && <Button size="sm" variant="outline" onClick={() => setExtLease(l)} data-testid={`extend-button-${l.id}`}><CalendarPlus className="mr-1.5 h-4 w-4" /> Perpanjang</Button>}
                      {l.status === "active" && <Button size="sm" variant="outline" className="text-destructive" onClick={() => setExitLease(l)} data-testid={`exit-button-${l.id}`}><LogOut className="mr-1.5 h-4 w-4" /> Keluar Awal</Button>}
                      {(l.status === "ended" || l.status === "early_exit") && Number(l.deposit) > 0 && !l.deposit_refunded && (
                        <Button size="sm" variant="outline" onClick={() => setDepositLease(l)} data-testid={`deposit-refund-button-${l.id}`}><Undo2 className="mr-1.5 h-4 w-4" /> Kembalikan Deposit</Button>
                      )}
                      {Number(l.deposit) > 0 && l.deposit_refunded && (
                        <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700" data-testid={`deposit-refunded-badge-${l.id}`}>Deposit dikembalikan</span>
                      )}
                    </div>
                  </div>
                </SectionCard>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="riwayat" className="mt-4">
          {bLoading ? <Loading /> : historyBookings.length === 0 ? (
            <EmptyState title="Belum ada riwayat booking" description="Booking yang dibatalkan atau kedaluwarsa (DP hangus) akan muncul di sini." icon={Ban} />
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-xl border border-border bg-muted/30 p-4 text-sm" data-testid="riwayat-summary">
                <span className="text-muted-foreground">Total DP Hangus (pendapatan pengelola)</span>
                <span className="text-lg font-bold" style={{ color: "#b91c1c" }}><Money value={totalForfeit} /></span>
              </div>
              {historyBookings.map((b) => {
                const st = BK_STATUS[b.status] || { label: b.status, c: "#475569", bg: "#f1f5f9" };
                return (
                  <SectionCard key={b.id} className="!p-4" testid={`history-row-${b.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{b.resident_name}</span>
                          <span className="rounded-full px-2 py-0.5 text-xs font-semibold" style={{ color: st.c, backgroundColor: st.bg }}>{st.label}</span>
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">{b.project_name} · {b.house_name} · Kamar {b.room_number} · {b.package_months} bln · {b.whatsapp}</div>
                        {b.close_reason && <div className="mt-1 text-xs text-muted-foreground">Alasan: {b.close_reason}</div>}
                        <div className="text-xs text-muted-foreground">{b.closed_at ? `Ditutup: ${formatTanggalWaktu(b.closed_at)}` : `Batas: ${formatTanggalWaktu(b.deadline_at)}`}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-muted-foreground">DP Hangus</div>
                        <div className="font-bold" style={{ color: "#b91c1c" }}><Money value={b.dp_amount} /></div>
                      </div>
                    </div>
                  </SectionCard>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {bookingOpen && <BookingDialog preRoomId={preRoom} onClose={() => setBookingOpen(false)} onDone={() => { setBookingOpen(false); qc.invalidateQueries(); }} />}
      {settleBooking && <SettlementDialog booking={settleBooking} onClose={() => setSettleBooking(null)} onDone={() => { setSettleBooking(null); qc.invalidateQueries(); }} />}
      {moveinLease && <MoveinDialog lease={moveinLease} onClose={() => setMoveinLease(null)} onDone={() => { setMoveinLease(null); qc.invalidateQueries(); }} />}
      {extLease && <ExtensionDialog lease={extLease} onClose={() => setExtLease(null)} onDone={() => { setExtLease(null); qc.invalidateQueries(); }} />}
      {exitLease && <ExitDialog lease={exitLease} onClose={() => setExitLease(null)} onDone={() => { setExitLease(null); qc.invalidateQueries(); }} />}
      {depositLease && <DepositRefundDialog lease={depositLease} onClose={() => setDepositLease(null)} onDone={() => { setDepositLease(null); qc.invalidateQueries(); }} />}
      {cancelBooking && <CancelBookingDialog booking={cancelBooking} onClose={() => setCancelBooking(null)} onDone={() => { setCancelBooking(null); qc.invalidateQueries(); }} />}
      {directOpen && <DirectRentDialog onClose={() => setDirectOpen(false)} onDone={() => { setDirectOpen(false); qc.invalidateQueries(); }} />}
    </div>
  );
}

function DueBadge({ lease }) {
  if (lease.status !== "active" || !lease.lease_end_exclusive) return null;
  const end = new Date(lease.lease_end_exclusive);
  end.setDate(end.getDate() - 1);
  end.setHours(0, 0, 0, 0);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const days = Math.round((end.getTime() - now.getTime()) / 86400000);
  if (days > 14) return null;
  const overdue = days < 0;
  return (
    <span className="rounded-full px-2 py-0.5 text-xs font-semibold"
      style={{ color: overdue ? "#b91c1c" : "#c2410c", backgroundColor: overdue ? "#fef2f2" : "#fff7ed" }}
      data-testid={`lease-due-badge-${lease.id}`}>
      {overdue ? `Terlewat ${Math.abs(days)} hari` : days === 0 ? "Berakhir hari ini" : `Berakhir ${days} hari lagi`}
    </span>
  );
}

function DepositRefundDialog({ lease, onClose, onDone }) {
  const [f, setF] = useState({ amount: String(lease.deposit || 0), date: new Date().toISOString().slice(0, 10), method: "transfer", note: "" });
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    const amt = Number(f.amount);
    if (!amt || amt <= 0 || amt > Number(lease.deposit)) { toast.error(`Nominal harus antara Rp1 dan deposit (${lease.deposit}).`); return; }
    setLoading(true);
    try {
      await api.post(`/leases/${lease.id}/deposit-refund`, { amount: amt, date: f.date, method: f.method, note: f.note });
      toast.success("Pengembalian deposit tercatat & masuk Keuangan Operasional (kas keluar).");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="deposit-refund-dialog">
        <DialogHeader><DialogTitle>Kembalikan Deposit — {lease.resident_name}</DialogTitle></DialogHeader>
        <div className="rounded-lg bg-muted/40 p-3 text-sm">
          Kamar {lease.room_number} · Deposit ditahan: <b><Money value={lease.deposit} /></b>
          <div className="mt-0.5 text-xs text-muted-foreground">Nominal dapat dikurangi (potongan kerusakan dll). Dana tercatat keluar dari kas di Keuangan Operasional.</div>
        </div>
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label>Nominal Dikembalikan (Rp)</Label><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="deposit-refund-amount" /></div>
            <div><Label>Tanggal</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="deposit-refund-date" /></div>
          </div>
          <div>
            <Label>Metode</Label>
            <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
              <SelectTrigger data-testid="deposit-refund-method"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="transfer">Transfer</SelectItem><SelectItem value="cash">Tunai</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Catatan (opsional)</Label><Textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="deposit-refund-note" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={loading} data-testid="deposit-refund-confirm">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Catat Pengembalian</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelBookingDialog({ booking, onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      await api.post(`/bookings/${booking.id}/cancel`);
      toast.success("Booking dibatalkan. DP hangus & tercatat sebagai pendapatan pengelola.");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="cancel-booking-dialog">
        <DialogHeader><DialogTitle>Batalkan Booking — {booking.resident_name}</DialogTitle></DialogHeader>
        <div className="rounded-lg border p-3 text-sm" style={{ backgroundColor: "#fef2f2", borderColor: "#fecaca", color: "#b91c1c" }}>
          DP sebesar <b><Money value={booking.dp_amount} /></b> akan <b>hangus</b> dan dicatat sebagai <b>pendapatan pengelola</b> (DP Hangus), bukan bagian investor. Kamar {booking.room_number} akan kembali tersedia. Tindakan ini tidak dapat dibatalkan.
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={submit} disabled={loading} data-testid="cancel-booking-confirm">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Ya, Batalkan &amp; Hanguskan DP
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryRow({ label, value, bold }) {
  return (
    <div className={`flex justify-between ${bold ? "font-semibold" : ""}`}>
      <span className={bold ? "" : "text-muted-foreground"}>{label}</span>
      <Money value={value} />
    </div>
  );
}

function BookingDialog({ preRoomId, onClose, onDone }) {
  const { projects } = useProjects();
  const qc = useQueryClient();
  const idemKey = useRef(uuid());
  const [projectId, setProjectId] = useState("");
  const [houseId, setHouseId] = useState("");
  const [roomId, setRoomId] = useState("");
  const [pkg, setPkg] = useState("3");
  const [money, setMoney] = useState({ discount: "", extra_fees: "", deposit: "1000000" });
  const [form, setForm] = useState({ name: "", whatsapp: "", method: "transfer", received: wibLocalNow(), reference: "", note: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (preRoomId) {
      api.get(`/rooms/${preRoomId}`).then(({ data }) => {
        setProjectId(data.room.project_id); setHouseId(data.room.house_id); setRoomId(preRoomId);
      });
    }
  }, [preRoomId]);

  const { data: houses = [] } = useQuery({ queryKey: ["houses", projectId], queryFn: async () => (await api.get("/houses", { params: { project_id: projectId } })).data, enabled: !!projectId });
  const { data: rooms = [] } = useQuery({ queryKey: ["rooms", houseId], queryFn: async () => (await api.get("/rooms", { params: { house_id: houseId } })).data, enabled: !!houseId });
  const availableRooms = rooms.filter((r) => r.rental_status === "tersedia" || r.id === roomId);
  const room = rooms.find((r) => r.id === roomId);

  const calc = useMemo(() => {
    if (!room) return null;
    const rent = Number(room.pricing[pkg] || 0);
    const discount = Math.max(0, Number(money.discount || 0));
    const extra = Math.max(0, Number(money.extra_fees || 0));
    const deposit = Math.max(0, Number(money.deposit || 0));
    const rentNet = Math.max(0, rent - discount);
    const ipl = Number(room.ipl_monthly) * Number(pkg);
    const total = rentNet + ipl + extra + deposit;
    return { rent, discount, extra, deposit, rentNet, ipl, total, dp: 500000, sisa: total - 500000 };
  }, [room, pkg, money]);

  const pkgOptions = room ? ["1", "3", "6", "12"].filter((m) => Number(room.pricing?.[m]) > 0) : ["1", "3", "6", "12"];
  useEffect(() => {
    if (!room) return;
    const opts = ["1", "3", "6", "12"].filter((m) => Number(room.pricing?.[m]) > 0);
    if (!opts.includes(pkg)) setPkg(opts[0] || "3");
  }, [roomId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!roomId || !form.name.trim() || !form.whatsapp.trim()) { toast.error("Lengkapi kamar, nama, dan WhatsApp."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        room_id: roomId, resident_name: form.name, whatsapp: form.whatsapp, package_months: Number(pkg),
        discount: calc.discount, extra_fees: calc.extra, deposit: calc.deposit,
        dp_method: form.method, dp_received_at: toIsoWib(form.received), reference: form.reference, note: form.note,
        idempotency_key: idemKey.current,
      });
      toast.success("Booking dibuat & struk DP diterbitkan.", { description: `Nomor struk: ${data.receipt_number}` });
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader><DialogTitle>Buat Booking Baru</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!preRoomId && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <Label>Proyek</Label>
                <Select value={projectId} onValueChange={(v) => { setProjectId(v); setHouseId(""); setRoomId(""); }}>
                  <SelectTrigger data-testid="booking-project"><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Rumah</Label>
                <Select value={houseId} onValueChange={(v) => { setHouseId(v); setRoomId(""); }} disabled={!projectId}>
                  <SelectTrigger data-testid="booking-house"><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>{houses.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Kamar</Label>
                <Select value={roomId} onValueChange={setRoomId} disabled={!houseId}>
                  <SelectTrigger data-testid="booking-room"><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>{availableRooms.map((r) => <SelectItem key={r.id} value={r.id}>Kamar {r.number}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          {preRoomId && room && <div className="rounded-lg bg-muted/40 p-2 text-sm">Kamar <b>{room.number}</b></div>}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label>Nama Calon Penghuni</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-name" /></div>
            <div><Label>Nomor WhatsApp</Label><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="booking-whatsapp" /></div>
          </div>

          <div>
            <Label>Paket Sewa</Label>
            <div className="mt-1 flex gap-2">
              {pkgOptions.map((m) => (
                <button key={m} onClick={() => setPkg(m)} data-testid={`booking-pkg-${m}`}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium ${pkg === m ? "border-primary bg-primary text-white" : "border-border bg-card"}`}>{m} bulan</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div><Label>Diskon (Rp)</Label><Input type="number" value={money.discount} onChange={(e) => setMoney({ ...money, discount: e.target.value })} placeholder="0" data-testid="booking-discount" /></div>
            <div><Label>Biaya Tambahan (Rp)</Label><Input type="number" value={money.extra_fees} onChange={(e) => setMoney({ ...money, extra_fees: e.target.value })} placeholder="0" data-testid="booking-extra" /></div>
            <div><Label>Deposit (Rp)</Label><Input type="number" value={money.deposit} onChange={(e) => setMoney({ ...money, deposit: e.target.value })} data-testid="booking-deposit" /></div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label>Metode DP</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger data-testid="booking-method"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="transfer">Transfer</SelectItem><SelectItem value="cash">Tunai</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Waktu DP Diterima (WIB)</Label><Input type="datetime-local" value={form.received} onChange={(e) => setForm({ ...form, received: e.target.value })} data-testid="booking-received" /></div>
          </div>
          <div><Label>Referensi (opsional)</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="booking-reference" /></div>

          {calc && (
            <div className="rounded-xl border border-border bg-muted/30 p-4 text-sm" data-testid="booking-summary">
              <div className="mb-2 font-semibold">Ringkasan</div>
              <div className="space-y-1">
                <SummaryRow label="Harga Sewa" value={calc.rent} />
                {calc.discount > 0 && <SummaryRow label="Diskon" value={-calc.discount} />}
                <SummaryRow label={`IPL (${Number(pkg)} bln)`} value={calc.ipl} />
                {calc.extra > 0 && <SummaryRow label="Biaya Tambahan" value={calc.extra} />}
                {calc.deposit > 0 && <SummaryRow label="Deposit (refundable)" value={calc.deposit} />}
                <SummaryRow label="Total Tagihan" value={calc.total} bold />
                <SummaryRow label="DP" value={calc.dp} />
                <SummaryRow label="Sisa Pelunasan" value={calc.sisa} bold />
              </div>
              <div className="mt-2 text-xs text-muted-foreground">Kamar ditahan 96 jam sejak DP diterima. DP menjadi bagian pembayaran sewa.</div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={loading || !calc} data-testid="booking-submit-button">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Simpan Booking & Terbitkan Struk DP
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SettlementDialog({ booking, onClose, onDone }) {
  const idemKey = useRef(uuid());
  const [f, setF] = useState({ full_name_ktp: booking.resident_name, nik: "", address_ktp: "", guardian_whatsapp: "", whatsapp: booking.whatsapp, method: "transfer", received: wibLocalNow(), plan: "", reference: "", note: "" });
  const [ktp, setKtp] = useState(null);
  const [loading, setLoading] = useState(false);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Ukuran KTP maksimal 5 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setKtp({ base64: reader.result, mime: file.type, name: file.name });
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!/^\d{16}$/.test(f.nik.trim())) { toast.error("NIK harus 16 digit angka."); return; }
    if (!f.full_name_ktp.trim() || !f.address_ktp.trim() || !f.guardian_whatsapp.trim()) { toast.error("Lengkapi data identitas wajib."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/settlements", {
        booking_id: booking.id, amount: booking.remaining, method: f.method, received_at: toIsoWib(f.received),
        reference: f.reference, note: f.note, plan_movein_date: f.plan || null,
        full_name_ktp: f.full_name_ktp, nik: f.nik, address_ktp: f.address_ktp, guardian_whatsapp: f.guardian_whatsapp,
        whatsapp: f.whatsapp, ktp_base64: ktp?.base64, ktp_mime: ktp?.mime, idempotency_key: idemKey.current,
      });
      toast.success("Pelunasan tercatat — status menjadi Menunggu Masuk.", { description: `Struk: ${data.receipt_number}` });
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="settlement-dialog">
        <DialogHeader><DialogTitle>Catat Pelunasan — {booking.resident_name}</DialogTitle></DialogHeader>
        <div className="rounded-lg bg-muted/40 p-3 text-sm" data-testid="settle-breakdown">
          <div className="mb-1 font-medium">Kamar {booking.room_number} · {booking.package_months} bln</div>
          <div className="space-y-0.5">
            <div className="flex justify-between"><span className="text-muted-foreground">Harga Sewa</span><Money value={booking.rent_price} /></div>
            {Number(booking.discount) > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Diskon</span><Money value={-Number(booking.discount)} /></div>}
            <div className="flex justify-between"><span className="text-muted-foreground">IPL ({booking.package_months} bln)</span><Money value={booking.ipl_total} /></div>
            {Number(booking.extra_fees) > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Biaya Tambahan</span><Money value={booking.extra_fees} /></div>}
            {Number(booking.deposit) > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Deposit (refundable)</span><Money value={booking.deposit} /></div>}
            <div className="flex justify-between"><span className="text-muted-foreground">Total Tagihan</span><Money value={booking.total_billed} /></div>
            <div className="flex justify-between"><span className="text-muted-foreground">DP dibayar</span><Money value={booking.dp_amount} /></div>
            <div className="flex justify-between font-semibold"><span>Sisa tagihan (pelunasan)</span><Money value={booking.remaining} /></div>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">Pelunasan harus sama dengan sisa tagihan.</div>
        </div>
        <div className="mt-3 space-y-3">
          <div className="text-xs font-semibold uppercase text-muted-foreground">Data Identitas (wajib saat pelunasan)</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label>Nama sesuai KTP</Label><Input value={f.full_name_ktp} onChange={(e) => setF({ ...f, full_name_ktp: e.target.value })} data-testid="settle-fullname" /></div>
            <div><Label>NIK (16 digit)</Label><Input value={f.nik} onChange={(e) => setF({ ...f, nik: e.target.value })} data-testid="settle-nik" /></div>
          </div>
          <div><Label>Alamat sesuai KTP</Label><Textarea value={f.address_ktp} onChange={(e) => setF({ ...f, address_ktp: e.target.value })} data-testid="settle-address" /></div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label>WhatsApp Penghuni</Label><Input value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} data-testid="settle-whatsapp" /></div>
            <div><Label>WhatsApp Wali</Label><Input value={f.guardian_whatsapp} onChange={(e) => setF({ ...f, guardian_whatsapp: e.target.value })} data-testid="settle-guardian" /></div>
          </div>
          <div>
            <Label>Foto / Scan KTP (JPG, PNG, PDF)</Label>
            <Input type="file" accept="image/jpeg,image/png,application/pdf" onChange={onFile} data-testid="settle-ktp-file" />
            {ktp && <div className="mt-1 text-xs text-muted-foreground">Terpilih: {ktp.name}</div>}
          </div>
          <div className="text-xs font-semibold uppercase text-muted-foreground">Pembayaran</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label>Metode</Label>
              <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
                <SelectTrigger data-testid="settle-method"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="transfer">Transfer</SelectItem><SelectItem value="cash">Tunai</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Waktu Diterima (WIB)</Label><Input type="datetime-local" value={f.received} onChange={(e) => setF({ ...f, received: e.target.value })} data-testid="settle-received" /></div>
          </div>
          <div><Label>Rencana Masuk (opsional)</Label><Input type="date" value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })} data-testid="settle-plan" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={loading} data-testid="settle-submit-button">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Catat Pelunasan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MoveinDialog({ lease, onClose, onDone }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try { const { data } = await api.post(`/leases/${lease.id}/movein`, { actual_movein_date: date }); toast.success("Penghuni tercatat masuk.", { description: `Periode: ${data.period_label}` }); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="movein-dialog">
        <DialogHeader><DialogTitle>Catat Pindah Masuk</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Masa sewa dimulai pada tanggal masuk aktual yang Anda catat.</p>
        <div><Label>Tanggal Masuk Aktual</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="movein-date" /></div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={submit} disabled={loading} data-testid="movein-submit">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExtensionDialog({ lease, onClose, onDone }) {
  const qc = useQueryClient();
  const idemKey = useRef(uuid());
  const [pkg, setPkg] = useState("3");
  const [method, setMethod] = useState("transfer");
  const [received, setReceived] = useState(wibLocalNow());
  const [loading, setLoading] = useState(false);
  const [customRent, setCustomRent] = useState("");
  const { data: roomDetail } = useQuery({ queryKey: ["room", lease.room_id], queryFn: async () => (await api.get(`/rooms/${lease.room_id}`)).data, enabled: !!lease.room_id });
  const { data: overrides = [] } = useQuery({ queryKey: ["price-overrides", lease.id], queryFn: async () => (await api.get(`/leases/${lease.id}/price-overrides`)).data });
  const room = roomDetail?.room;
  const override = overrides.find((o) => String(o.package_months) === pkg && o.status === "active");
  const baseRent = override ? Number(override.rent_price) : (room ? Number(room.pricing[pkg]) : 0);
  const rent = customRent !== "" ? Number(customRent) || 0 : baseRent;
  const iplTotal = room ? Number(room.ipl_monthly) * Number(pkg) : 0;
  const amount = room ? rent + iplTotal : 0;
  const extPkgOptions = room ? ["1", "3", "6", "12"].filter((m) => Number(room.pricing?.[m]) > 0) : ["1", "3", "6", "12"];

  const submit = async () => {
    if (rent <= 0) { toast.error("Harga sewa perpanjangan tidak valid."); return; }
    setLoading(true);
    try {
      const body = { package_months: Number(pkg), amount, method, received_at: toIsoWib(received), idempotency_key: idemKey.current };
      if (customRent !== "" && rent !== baseRent) body.rent_price = rent;
      await api.post(`/leases/${lease.id}/extensions`, body);
      toast.success("Perpanjangan tercatat & lunas."); qc.invalidateQueries({ queryKey: ["price-overrides", lease.id] }); onDone();
    }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="extension-dialog">
        <DialogHeader><DialogTitle>Perpanjangan Sewa — {lease.resident_name}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Periode baru menyambung periode berjalan. Dibayar penuh (tanpa DP).</p>
        <div>
          <Label>Durasi</Label>
          <div className="mt-1 flex gap-2">
            {extPkgOptions.map((m) => <button key={m} onClick={() => { setPkg(m); setCustomRent(""); }} data-testid={`ext-pkg-${m}`} className={`flex-1 rounded-lg border px-3 py-2 text-sm ${pkg === m ? "border-primary bg-primary text-white" : "border-border"}`}>{m} bln</button>)}
          </div>
        </div>
        {override && (
          <div className="rounded-lg border p-3 text-sm" style={{ backgroundColor: "#f0fdf4", borderColor: "#bbf7d0", color: "#15803d" }} data-testid="ext-override-banner">
            Harga khusus disetujui: <b><Money value={override.rent_price} /></b> untuk {pkg} bln — terpasang otomatis.
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Metode</Label>
            <Select value={method} onValueChange={setMethod}><SelectTrigger data-testid="ext-method"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="transfer">Transfer</SelectItem><SelectItem value="cash">Tunai</SelectItem></SelectContent></Select>
          </div>
          <div><Label>Waktu Diterima (WIB)</Label><Input type="datetime-local" value={received} onChange={(e) => setReceived(e.target.value)} data-testid="ext-received" /></div>
        </div>
        <div>
          <Label>Harga Sewa {pkg} bln — bisa diubah</Label>
          <Input type="number" min="1" value={customRent !== "" ? customRent : (baseRent || "")} onChange={(e) => setCustomRent(e.target.value)} data-testid="ext-rent-price" />
          {customRent !== "" && rent !== baseRent && (
            <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground" data-testid="ext-rent-custom-note">
              <span>Harga normal: <Money value={baseRent} /></span>
              <button className="font-medium text-primary hover:underline" onClick={() => setCustomRent("")} data-testid="ext-rent-reset">Kembalikan harga normal</button>
            </div>
          )}
        </div>
        <div className="rounded-lg bg-muted/40 p-3 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Harga sewa {pkg} bln{override || rent !== Number(room?.pricing?.[pkg] || 0) ? " (khusus)" : ""}</span><Money value={rent} /></div>
          <div className="flex justify-between"><span className="text-muted-foreground">IPL ({pkg} bln)</span><Money value={iplTotal} /></div>
          <div className="mt-1 flex justify-between font-semibold"><span>Total pelunasan</span><Money value={amount} /></div>
        </div>
        {overrides.length > 0 && (
          <div className="rounded-lg border border-border p-3" data-testid="ext-override-history">
            <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Riwayat Harga Khusus</div>
            <div className="space-y-1">
              {overrides.map((o) => (
                <div key={o.id} className="flex items-center justify-between text-xs" data-testid={`ext-override-history-${o.id}`}>
                  <span>{o.package_months} bln · <Money value={o.rent_price} /> · oleh {o.created_by}</span>
                  <span className="rounded-full px-2 py-0.5 font-medium" style={o.status === "active" ? { color: "#047857", backgroundColor: "#ecfdf5" } : { color: "#475569", backgroundColor: "#f1f5f9" }}>
                    {o.status === "active" ? "Aktif" : "Terpakai"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={submit} disabled={loading || !room} data-testid="ext-submit">Catat Perpanjangan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExitDialog({ lease, onClose, onDone }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [conflict, setConflict] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Alasan wajib diisi."); return; }
    setLoading(true);
    try { await api.post(`/leases/${lease.id}/checkout-early`, { actual_checkout_date: date, reason }); toast.success("Penghuni keluar dicatat. Kamar kembali tersedia."); onDone(); }
    catch (e) {
      if (e?.response?.status === 409 && e?.response?.data?.detail?.code === "exit_conflict") setConflict(true);
      else toast.error(errMsg(e));
    } finally { setLoading(false); }
  };
  const raiseConflict = async () => {
    setLoading(true);
    try { await api.post(`/leases/${lease.id}/exit-conflict`, { actual_checkout_date: date, reason }); toast.success("Konflik diajukan. Menunggu keputusan superadmin."); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="exit-dialog">
        <DialogHeader><DialogTitle>Keluar Lebih Awal — {lease.resident_name}</DialogTitle></DialogHeader>
        {conflict ? (
          <div className="space-y-3">
            <div className="rounded-lg border p-3 text-sm" style={{ backgroundColor: "#fef2f2", borderColor: "#fecaca", color: "#b91c1c" }}>
              Terdapat <b>perpanjangan yang sudah lunas</b> untuk sewa ini. Keluar lebih awal tidak dapat langsung diproses dan tidak boleh menghanguskan transaksi masa depan secara diam-diam. Ajukan penyelesaian agar superadmin memutuskan.
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Tutup</Button>
              <Button onClick={raiseConflict} disabled={loading} data-testid="exit-raise-conflict">Ajukan Penyelesaian ke Superadmin</Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">Tidak ada refund sewa maupun IPL. Riwayat & akhir kontrak semula tetap tersimpan.</p>
            <div><Label>Tanggal Keluar Aktual</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="exit-date" /></div>
            <div><Label>Alasan</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="exit-reason" /></div>
            <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={submit} disabled={loading} data-testid="exit-submit">Proses Keluar</Button></DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DirectRentDialog({ onClose, onDone }) {
  const { projects } = useProjects();
  const idemKey = useRef(uuid());
  const [projectId, setProjectId] = useState("");
  const [houseId, setHouseId] = useState("");
  const [roomId, setRoomId] = useState("");
  const [pkg, setPkg] = useState("3");
  const [mode, setMode] = useState("new");
  const [money, setMoney] = useState({ discount: "", extra_fees: "", deposit: "1000000" });
  const [residentId, setResidentId] = useState("");
  const [f, setF] = useState({
    name: "", whatsapp: "", full_name_ktp: "", nik: "", address_ktp: "", guardian_whatsapp: "",
    method: "transfer", received: wibLocalNow(), start: new Date().toISOString().slice(0, 10), reference: "", note: "",
  });
  const [ktp, setKtp] = useState(null);
  const [loading, setLoading] = useState(false);

  const { data: houses = [] } = useQuery({ queryKey: ["houses", projectId], queryFn: async () => (await api.get("/houses", { params: { project_id: projectId } })).data, enabled: !!projectId });
  const { data: rooms = [] } = useQuery({ queryKey: ["rooms", houseId], queryFn: async () => (await api.get("/rooms", { params: { house_id: houseId } })).data, enabled: !!houseId });
  const { data: residentsData } = useQuery({ queryKey: ["residents-direct", projectId], queryFn: async () => (await api.get("/residents", { params: { project_id: projectId, page_size: 200 } })).data, enabled: mode === "existing" && !!projectId });
  const residents = residentsData?.items || [];
  const availableRooms = rooms.filter((r) => r.rental_status === "tersedia");
  const room = rooms.find((r) => r.id === roomId);

  const calc = useMemo(() => {
    if (!room) return null;
    const rent = Number(room.pricing[pkg] || 0);
    const discount = Math.max(0, Number(money.discount || 0));
    const extra = Math.max(0, Number(money.extra_fees || 0));
    const deposit = Math.max(0, Number(money.deposit || 0));
    const rentNet = Math.max(0, rent - discount);
    const ipl = Number(room.ipl_monthly) * Number(pkg);
    return { rent, discount, extra, deposit, rentNet, ipl, total: rentNet + ipl + extra + deposit };
  }, [room, pkg, money]);

  const pkgOptions = room ? ["1", "3", "6", "12"].filter((m) => Number(room.pricing?.[m]) > 0) : ["1", "3", "6", "12"];
  useEffect(() => {
    if (!room) return;
    const opts = ["1", "3", "6", "12"].filter((m) => Number(room.pricing?.[m]) > 0);
    if (!opts.includes(pkg)) setPkg(opts[0] || "3");
  }, [roomId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Ukuran KTP maksimal 5 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setKtp({ base64: reader.result, mime: file.type, name: file.name });
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!roomId) { toast.error("Pilih kamar terlebih dahulu."); return; }
    if (mode === "existing") {
      if (!residentId) { toast.error("Pilih penghuni yang sudah ada."); return; }
    } else {
      if (!f.name.trim() || !f.whatsapp.trim()) { toast.error("Lengkapi nama & WhatsApp penghuni."); return; }
      if (!/^\d{16}$/.test(f.nik.trim())) { toast.error("NIK harus 16 digit angka."); return; }
      if (!f.full_name_ktp.trim() || !f.address_ktp.trim() || !f.guardian_whatsapp.trim()) { toast.error("Lengkapi data identitas wajib (nama KTP, alamat, WhatsApp wali)."); return; }
    }
    setLoading(true);
    try {
      const payload = {
        room_id: roomId, package_months: Number(pkg), start_date: f.start, amount: calc.total,
        discount: calc.discount, extra_fees: calc.extra, deposit: calc.deposit,
        method: f.method, received_at: toIsoWib(f.received),
        reference: f.reference, note: f.note, idempotency_key: idemKey.current,
      };
      if (mode === "existing") {
        payload.resident_id = residentId;
      } else {
        Object.assign(payload, {
          name: f.name, whatsapp: f.whatsapp, full_name_ktp: f.full_name_ktp, nik: f.nik,
          address_ktp: f.address_ktp, guardian_whatsapp: f.guardian_whatsapp,
          ktp_base64: ktp?.base64, ktp_mime: ktp?.mime,
        });
      }
      const { data } = await api.post("/direct-rent", payload);
      toast.success("Langsung Sewa tercatat — status Menunggu Masuk.", { description: `Struk: ${data.receipt_number}` });
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="direct-rent-dialog">
        <DialogHeader><DialogTitle>Langsung Sewa — Tanpa DP</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Penghuni membayar penuh (sewa + IPL) di awal tanpa DP. Sewa langsung berstatus Menunggu Masuk.</p>
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <Label>Proyek</Label>
              <Select value={projectId} onValueChange={(v) => { setProjectId(v); setHouseId(""); setRoomId(""); }}>
                <SelectTrigger data-testid="direct-project"><SelectValue placeholder="Pilih" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Rumah</Label>
              <Select value={houseId} onValueChange={(v) => { setHouseId(v); setRoomId(""); }} disabled={!projectId}>
                <SelectTrigger data-testid="direct-house"><SelectValue placeholder="Pilih" /></SelectTrigger>
                <SelectContent>{houses.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Kamar</Label>
              <Select value={roomId} onValueChange={setRoomId} disabled={!houseId}>
                <SelectTrigger data-testid="direct-room"><SelectValue placeholder="Pilih" /></SelectTrigger>
                <SelectContent>{availableRooms.map((r) => <SelectItem key={r.id} value={r.id}>Kamar {r.number}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Paket Sewa</Label>
            <div className="mt-1 flex gap-2">
              {pkgOptions.map((m) => (
                <button key={m} onClick={() => setPkg(m)} data-testid={`direct-pkg-${m}`}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium ${pkg === m ? "border-primary bg-primary text-white" : "border-border bg-card"}`}>{m} bulan</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div><Label>Diskon (Rp)</Label><Input type="number" value={money.discount} onChange={(e) => setMoney({ ...money, discount: e.target.value })} placeholder="0" data-testid="direct-discount" /></div>
            <div><Label>Biaya Tambahan (Rp)</Label><Input type="number" value={money.extra_fees} onChange={(e) => setMoney({ ...money, extra_fees: e.target.value })} placeholder="0" data-testid="direct-extra" /></div>
            <div><Label>Deposit (Rp)</Label><Input type="number" value={money.deposit} onChange={(e) => setMoney({ ...money, deposit: e.target.value })} data-testid="direct-deposit" /></div>
          </div>

          <div>
            <Label>Penghuni</Label>
            <div className="mt-1 flex gap-2">
              <button onClick={() => setMode("new")} data-testid="direct-mode-new"
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium ${mode === "new" ? "border-primary bg-primary text-white" : "border-border bg-card"}`}>Penghuni Baru</button>
              <button onClick={() => setMode("existing")} data-testid="direct-mode-existing"
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium ${mode === "existing" ? "border-primary bg-primary text-white" : "border-border bg-card"}`}>Penghuni Lama</button>
            </div>
          </div>

          {mode === "existing" ? (
            <div>
              <Label>Pilih Penghuni (dari proyek terpilih)</Label>
              <Select value={residentId} onValueChange={setResidentId} disabled={!projectId}>
                <SelectTrigger data-testid="direct-resident"><SelectValue placeholder={projectId ? "Pilih penghuni" : "Pilih proyek dahulu"} /></SelectTrigger>
                <SelectContent>{residents.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}{r.whatsapp ? ` · ${r.whatsapp}` : ""}</SelectItem>)}</SelectContent>
              </Select>
              {projectId && residents.length === 0 && <div className="mt-1 text-xs text-muted-foreground">Belum ada penghuni pada proyek ini.</div>}
            </div>
          ) : (
            <>
              <div className="text-xs font-semibold uppercase text-muted-foreground">Data Identitas Penghuni (wajib)</div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div><Label>Nama Penghuni</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="direct-name" /></div>
                <div><Label>Nomor WhatsApp</Label><Input value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} data-testid="direct-whatsapp" /></div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div><Label>Nama sesuai KTP</Label><Input value={f.full_name_ktp} onChange={(e) => setF({ ...f, full_name_ktp: e.target.value })} data-testid="direct-fullname" /></div>
                <div><Label>NIK (16 digit)</Label><Input value={f.nik} onChange={(e) => setF({ ...f, nik: e.target.value })} data-testid="direct-nik" /></div>
              </div>
              <div><Label>Alamat sesuai KTP</Label><Textarea value={f.address_ktp} onChange={(e) => setF({ ...f, address_ktp: e.target.value })} data-testid="direct-address" /></div>
              <div><Label>WhatsApp Wali</Label><Input value={f.guardian_whatsapp} onChange={(e) => setF({ ...f, guardian_whatsapp: e.target.value })} data-testid="direct-guardian" /></div>
              <div>
                <Label>Foto / Scan KTP (JPG, PNG, PDF)</Label>
                <Input type="file" accept="image/jpeg,image/png,application/pdf" onChange={onFile} data-testid="direct-ktp-file" />
                {ktp && <div className="mt-1 text-xs text-muted-foreground">Terpilih: {ktp.name}</div>}
              </div>
            </>
          )}

          <div className="text-xs font-semibold uppercase text-muted-foreground">Pembayaran & Jadwal</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label>Metode</Label>
              <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
                <SelectTrigger data-testid="direct-method"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="transfer">Transfer</SelectItem><SelectItem value="cash">Tunai</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Waktu Diterima (WIB)</Label><Input type="datetime-local" value={f.received} onChange={(e) => setF({ ...f, received: e.target.value })} data-testid="direct-received" /></div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label>Rencana Masuk</Label><Input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="direct-start" /></div>
            <div><Label>Referensi (opsional)</Label><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} data-testid="direct-reference" /></div>
          </div>

          {calc && (
            <div className="rounded-xl border border-border bg-muted/30 p-4 text-sm" data-testid="direct-summary">
              <div className="mb-2 font-semibold">Ringkasan</div>
              <div className="space-y-1">
                <SummaryRow label="Harga Sewa" value={calc.rent} />
                {calc.discount > 0 && <SummaryRow label="Diskon" value={-calc.discount} />}
                <SummaryRow label={`IPL (${Number(pkg)} bln)`} value={calc.ipl} />
                {calc.extra > 0 && <SummaryRow label="Biaya Tambahan" value={calc.extra} />}
                {calc.deposit > 0 && <SummaryRow label="Deposit (refundable)" value={calc.deposit} />}
                <SummaryRow label="Total Bayar (Penuh, Tanpa DP)" value={calc.total} bold />
              </div>
              <div className="mt-2 text-xs text-muted-foreground">Dibayar penuh di awal. Tidak ada DP dan tidak ada sisa pelunasan.</div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={loading || !calc} data-testid="direct-submit-button">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Simpan Langsung Sewa & Terbitkan Struk
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

